import React, { useCallback, useEffect, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import {
  listOrgSecrets,
  upsertOrgSecret,
  revealOrgSecret,
  deleteOrgSecret,
  type ScopedSecretMetadata,
} from "src/domains/application/module/api/secretsApi";
import { useNotification } from "src/shell/contexts/notificationContext";
import { sectionLabelSx } from "src/shell/styles/themeUtils";

// ── Types ─────────────────────────────────────────────────────────────────────

interface OrgSecretsTabProps {
  orgAlias: string;
}

function relativeTime(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * OrgSecretsTab — admin-managed organization secrets inherited by every
 * project and stack in the org.
 */
export function OrgSecretsTab({
  orgAlias,
}: OrgSecretsTabProps): React.ReactElement {
  const theme = useTheme();
  const { showNotification } = useNotification();
  const [secrets, setSecrets] = useState<ScopedSecretMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, string>>({});
  const [revealLoading, setRevealLoading] = useState<Set<string>>(new Set());

  // Dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string>("");
  const [formData, setFormData] = useState({ key: "", value: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchSecrets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listOrgSecrets(orgAlias);
      setSecrets(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load secrets");
    } finally {
      setLoading(false);
    }
  }, [orgAlias]);

  useEffect(() => {
    void fetchSecrets();
  }, [fetchSecrets]);

  const isVaultUnreachable =
    error !== null && error.includes("Vault is unreachable");

  const handleReveal = async (key: string) => {
    if (revealed[key]) {
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }
    setRevealLoading((prev) => new Set(prev).add(key));
    try {
      const value = await revealOrgSecret(orgAlias, key);
      setRevealed((prev) => ({ ...prev, [key]: value }));
    } catch (err) {
      showNotification(
        `Failed to reveal secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setRevealLoading((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleAdd = () => {
    setFormData({ key: "", value: "" });
    setAddDialogOpen(true);
  };

  const handleEdit = (secret: ScopedSecretMetadata) => {
    setActiveKey(secret.key);
    setFormData({ key: secret.key, value: "" });
    setEditDialogOpen(true);
  };

  const handleDelete = (key: string) => {
    setActiveKey(key);
    setDeleteDialogOpen(true);
  };

  const submitAdd = async () => {
    if (!formData.key.trim() || !formData.value.trim()) return;
    setSubmitting(true);
    try {
      await upsertOrgSecret(orgAlias, formData.key.trim(), formData.value);
      showNotification("Secret added successfully", "success");
      setAddDialogOpen(false);
      void fetchSecrets();
    } catch (err) {
      showNotification(
        `Failed to add secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitEdit = async () => {
    if (!formData.value.trim()) return;
    setSubmitting(true);
    try {
      await upsertOrgSecret(orgAlias, activeKey, formData.value);
      showNotification("Secret updated successfully", "success");
      setEditDialogOpen(false);
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[activeKey];
        return next;
      });
      void fetchSecrets();
    } catch (err) {
      showNotification(
        `Failed to update secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async () => {
    setSubmitting(true);
    try {
      await deleteOrgSecret(orgAlias, activeKey);
      showNotification("Secret deleted successfully", "success");
      setDeleteDialogOpen(false);
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[activeKey];
        return next;
      });
      void fetchSecrets();
    } catch (err) {
      showNotification(
        `Failed to delete secret: ${err instanceof Error ? err.message : "Unknown error"}`,
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Inheritance banner */}
      <Alert
        severity="info"
        icon={<LinkIcon fontSize="small" />}
        sx={{ mb: 2.5, borderLeft: `3px solid ${theme.palette.primary.main}` }}
      >
        These secrets are inherited by <strong>every project and stack</strong>{" "}
        in this organization. Only org admins can manage these.
      </Alert>

      {/* Section header */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={sectionLabelSx}>
          Organization Secrets
        </Typography>
        <Typography variant="body2" color="text.secondary">
          — shared across all projects
        </Typography>
        {secrets.length > 0 && (
          <Chip label={secrets.length} size="small" variant="outlined" />
        )}
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" size="small" onClick={handleAdd}>
          Add Secret
        </Button>
      </Stack>

      {loading ? (
        <LinearProgress />
      ) : error ? (
        isVaultUnreachable ? (
          <Alert
            severity="warning"
            icon={false}
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => void fetchSecrets()}
              >
                Try again
              </Button>
            }
          >
            Can&apos;t reach the secret store (Vault). This may be a temporary
            issue. Click retry to try again.
          </Alert>
        ) : (
          <Alert severity="error">{error}</Alert>
        )
      ) : secrets.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No organization secrets configured. Click &quot;Add Secret&quot; to
            add your first secret.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {secrets.map((secret) => (
                <TableRow
                  key={secret.key}
                  sx={{
                    "&:hover": {
                      bgcolor: theme.palette.action.hover,
                    },
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontWeight={600}
                    >
                      {secret.key}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      color="text.secondary"
                    >
                      {revealed[secret.key] ?? "••••••••••••••••"}
                    </Typography>
                  </TableCell>
                  <TableCell>{relativeTime(secret.updatedAt)}</TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                    >
                      <Tooltip
                        title={
                          revealed[secret.key] ? "Hide value" : "Reveal value"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleReveal(secret.key)}
                          disabled={revealLoading.has(secret.key)}
                        >
                          {revealLoading.has(secret.key) ? (
                            <CircularProgress size={16} />
                          ) : revealed[secret.key] ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(secret)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(secret.key)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Secret Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Organization Secret</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Secret Key"
            placeholder="SLACK_WEBHOOK_URL"
            value={formData.key}
            onChange={(e) =>
              setFormData((p) => ({ ...p, key: e.target.value }))
            }
            fullWidth
            inputProps={{ pattern: "^[A-Z][A-Z0-9_]*$" }}
            helperText="UPPER_SNAKE_CASE. Inherited by all projects and stacks in this org."
          />
          <TextField
            margin="dense"
            label="Secret Value"
            placeholder="Enter secret value..."
            value={formData.value}
            onChange={(e) =>
              setFormData((p) => ({ ...p, value: e.target.value }))
            }
            fullWidth
            multiline
            rows={3}
            helperText="Encrypted at rest in Vault. Never shown in list view."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitAdd}
            disabled={
              submitting || !formData.key.trim() || !formData.value.trim()
            }
          >
            {submitting ? <CircularProgress size={20} /> : "Add Secret"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Secret Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Organization Secret</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Secret Key"
            value={activeKey}
            disabled
            fullWidth
          />
          <TextField
            autoFocus
            margin="dense"
            label="New Secret Value"
            value={formData.value}
            onChange={(e) =>
              setFormData((p) => ({ ...p, value: e.target.value }))
            }
            fullWidth
            multiline
            rows={3}
            helperText="Enter the new value. The previous value will be overwritten."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={submitEdit}
            disabled={submitting || !formData.value.trim()}
          >
            {submitting ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Secret</DialogTitle>
        <DialogContent>
          <Typography>
            Delete secret{" "}
            <strong style={{ fontFamily: "monospace" }}>{activeKey}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. All projects and stacks inheriting it
            will lose this env var on the next deploy.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={submitDelete}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default OrgSecretsTab;
